import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { Modal, Table, Typography } from 'antd'
import fileDownload from 'js-file-download'
import { isNil, startCase, without } from 'lodash-es'
import moment from 'moment'

import { Button, Panel } from '../../../../core/components/styled'

import { LondonDateTime } from '../../../../common/components'
import {
  DEFAULT_PAGE,
  MENU_SCRAPING_JOB_STATUSES,
  SCRAPING_STATUSES,
} from '../../../../common/constants'
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../../common/helpers'

import { MENU_STATUSES } from '../../../../menus/constants/menu-statuses'
import { generateAdvancedProbeCrawlingSessionReport } from '../../../../reports/rest/advanced-probe-crawling-session'
import { generateMenuChangeFrequencyReport } from '../../../../reports/rest/menu-change-frequency'
import {
  ABORT_CRAWLING_SESSION,
  START_CRAWLING_SESSION,
} from '../../../graphql/mutations'
import {
  ADVANCED_PROBE_CRAWLING_SESSION_MENUS,
  ADVANCED_PROBE_CRAWLING_SESSIONS,
} from '../../../graphql/queries'

const SESSIONS_TABLE_PAGE_SIZE = 5
const SESSION_DETAILS_TABLE_PAGE_SIZE = 10

export const CRAWLING_SESSION_TYPES = {
  REGULAR: 'REGULAR',
  ADVANCED_PROBE_PDF: 'ADVANCED_PROBE_PDF',
  ADVANCED_PROBE_IMAGE: 'ADVANCED_PROBE_IMAGE',
}

const formatMenuStatus = status => {
  if (!status) return ''
  const label = MENU_STATUSES[status]?.name || status
  return startCase(label.toLowerCase())
}

const formatCrawlingJobStatus = status => {
  if (!status) return ''
  const label = MENU_SCRAPING_JOB_STATUSES[status]?.name || status
  return startCase(label.toLowerCase())
}

const formatCrawlingStatus = status => status && SCRAPING_STATUSES[status]?.name

const formatScrapingJobStatus = status => {
  if (!status) return ''
  const label = MENU_SCRAPING_JOB_STATUSES[status]?.name || status
  return startCase(label.toLowerCase())
}

const formatScrapingStatus = status => status && SCRAPING_STATUSES[status]?.name

const buildSessionMenusColumns = () => {
  return [
    {
      title: 'Menu ID',
      dataIndex: 'menuId',
      width: '5%',
      render: menuId => <Link to={`/menus/${menuId}`}>{menuId}</Link>,
    },
    {
      title: 'Menu status (Current)',
      dataIndex: 'menuStatus',
      width: '10%',
      render: formatMenuStatus,
    },
    {
      title: 'Crawling job status',
      dataIndex: 'crawlingJobStatus',
      width: '8%',
      render: formatCrawlingJobStatus,
    },
    {
      title: 'Crawling status',
      dataIndex: 'crawlingStatus',
      width: '8%',
      render: formatCrawlingStatus,
    },
    {
      title: 'Crawling schedule date',
      dataIndex: 'crawlingJobScheduleDate',
      width: '10%',
      render: date => <LondonDateTime date={date} />,
    },
    {
      title: 'Crawling completed date',
      dataIndex: 'crawlingJobCompletedDate',
      width: '10%',
      render: date => <LondonDateTime date={date} />,
    },
    {
      title: 'Crawling detected changes',
      dataIndex: 'crawlingDetectedChanges',
      width: '8%',
      render: value => {
        if (value === null || value === undefined) return ''
        return value ? 'Yes' : 'No'
      },
    },
    {
      title: 'Previously selected file exists',
      dataIndex: 'previouslySelectedFileExists',
      width: '10%',
      render: value => {
        if (isNil(value)) return ''
        return value ? 'Yes' : 'No'
      },
    },
    {
      title: 'Scraping job status',
      dataIndex: 'scrapingJobStatus',
      width: '10%',
      render: formatScrapingJobStatus,
    },
    {
      title: 'Scraping status',
      dataIndex: 'scrapingStatus',
      width: '10%',
      render: formatScrapingStatus,
    },
  ]
}

const createCrawlingSessionTab = ({ type }) => {
  const CrawlingSessionTab = () => {
    const [sessionsPage, setSessionsPage] = useState(DEFAULT_PAGE)
    const [sessionMenusPage, setSessionMenusPage] = useState(DEFAULT_PAGE)
    const [selectedSessionId, setSelectedSessionId] = useState()
    const [downloadingSessionId, setDownloadingSessionId] = useState()
    const [
      downloadingMenuChangeDetections,
      setDownloadingMenuChangeDetections,
    ] = useState(false)
    const [abortSessionIds, setAbortSessionIds] = useState([])

    const {
      data: {
        advancedProbeCrawlingSessions: {
          advancedProbeCrawlingSessions: sessionItems = [],
          total: sessionsTotal = 0,
        } = {},
      } = {},
      loading: isLoadingSessions,
      refetch: refetchSessions,
    } = useQuery(ADVANCED_PROBE_CRAWLING_SESSIONS, {
      variables: {
        page: sessionsPage,
        pageSize: SESSIONS_TABLE_PAGE_SIZE,
        type,
      },
      fetchPolicy: 'no-cache',
    })

    useEffect(() => {
      setSessionMenusPage(DEFAULT_PAGE)
    }, [selectedSessionId])

    const {
      data: {
        advancedProbeCrawlingSessionMenus: {
          advancedProbeCrawlingSessionMenus: sessionMenus = [],
          total: sessionMenusTotal = 0,
        } = {},
      } = {},
      loading: isLoadingSessionMenus,
    } = useQuery(ADVANCED_PROBE_CRAWLING_SESSION_MENUS, {
      variables: {
        sessionId: selectedSessionId,
        page: sessionMenusPage,
        pageSize: SESSION_DETAILS_TABLE_PAGE_SIZE,
      },
      skip: !selectedSessionId,
      fetchPolicy: 'no-cache',
    })

    const [startSession, { loading: isStartingSession }] = useMutation(
      START_CRAWLING_SESSION,
      {
        onCompleted({ startCrawlingSession: { id } = {} }) {
          showSuccessNotification({
            message: 'Advanced probe crawling session started.',
            description: `Advanced probe crawling session ${id} was started successfully.`,
          })
          setSelectedSessionId(id)
          setSessionsPage(DEFAULT_PAGE)
          refetchSessions()
        },
        onError({ message }) {
          showErrorNotification({
            message: 'Start advanced probe crawling session failed.',
            description: message,
          })
        },
      },
    )

    const [abortSessionMutation] = useMutation(ABORT_CRAWLING_SESSION)

    const handleStartSession = () => {
      startSession({
        variables: { type },
      })
    }

    const handleDownloadReport = useCallback(async sessionId => {
      try {
        setDownloadingSessionId(sessionId)
        const { data } = await generateAdvancedProbeCrawlingSessionReport({
          sessionId,
        })
        const timestamp = moment().format('YYYY-MM-DD-HH-mm-ss')
        fileDownload(
          data,
          `advanced-probe-crawling-session-${sessionId}-${timestamp}.zip`,
        )
      } catch (error) {
        const { response, message } = error || {}
        showErrorNotification({
          message: 'Download advanced probe crawling session report failed.',
          description: response?.statusText || message,
        })
      } finally {
        setDownloadingSessionId(undefined)
      }
    }, [])

    const handleDownloadMenuChangeDetections = useCallback(async () => {
      try {
        setDownloadingMenuChangeDetections(true)
        const menuType =
          type === CRAWLING_SESSION_TYPES.ADVANCED_PROBE_PDF ? 'pdf' : 'image'
        const { data } = await generateMenuChangeFrequencyReport({ menuType })
        const timestamp = moment().format('YYYY-MM-DD-HH-mm-ss')
        fileDownload(data, `menu-change-frequency-${menuType}-${timestamp}.csv`)
      } catch (error) {
        const { response, message } = error || {}
        showErrorNotification({
          message: 'Download menu change detections report failed.',
          description: response?.statusText || message,
        })
      } finally {
        setDownloadingMenuChangeDetections(false)
      }
    }, [])

    const handleAbortSession = useCallback(
      session => {
        const { id } = session

        Modal.confirm({
          title: 'Are you sure you want to abort this session?',
          content: `This will abort advanced probe crawling session ${id}. This action cannot be undone.`,
          async onOk() {
            setAbortSessionIds(prevIds => [...prevIds, id])
            try {
              await abortSessionMutation({ variables: { id } })
              showSuccessNotification({
                message: 'Advanced probe crawling session aborted.',
                description: `Advanced probe crawling session ${id} was aborted successfully.`,
              })
              setSelectedSessionId(undefined)
              refetchSessions()
            } catch (error) {
              const { response, message } = error || {}
              showErrorNotification({
                message: 'Abort advanced probe crawling session failed.',
                description: response?.statusText || message,
              })
            } finally {
              setAbortSessionIds(prevIds => without(prevIds, id))
            }
          },
        })
      },
      [abortSessionMutation, refetchSessions],
    )

    const sessionColumns = useMemo(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          width: '5%',
        },
        {
          title: 'Created date',
          dataIndex: 'createdDate',
          width: '10%',
          render: date => <LondonDateTime date={date} />,
        },
        {
          title: 'Created by ID',
          dataIndex: 'createdById',
          width: '10%',
        },
        {
          title: 'Aborted date',
          dataIndex: 'abortedDate',
          width: '10%',
          render: date => <LondonDateTime date={date} />,
        },
        {
          title: 'Aborted by ID',
          dataIndex: 'abortedById',
          width: '10%',
        },
        {
          title: 'Total menus',
          dataIndex: 'totalMenuCount',
          width: '10%',
        },
        {
          title: 'Crawled menus',
          dataIndex: 'crawledMenuCount',
          width: '10%',
        },
        {
          title: 'Scraped menus',
          dataIndex: 'scrapedMenuCount',
          width: '10%',
        },
        {
          title: 'Actions',
          key: 'actions',
          width: '20%',
          render: session => (
            <>
              <Button
                icon="download"
                margin="no small no no"
                loading={downloadingSessionId === session.id}
                onClick={event => {
                  event.stopPropagation()
                  handleDownloadReport(session.id)
                }}
              >
                Session report
              </Button>
              <Button
                icon="stop"
                margin="no"
                type="danger"
                disabled={
                  session.status !== 'IN_PROGRESS' ||
                  session.totalMenuCount === session.crawledMenuCount
                }
                loading={abortSessionIds.includes(session.id)}
                onClick={event => {
                  event.stopPropagation()
                  handleAbortSession(session)
                }}
              >
                Abort
              </Button>
            </>
          ),
        },
      ],
      [
        abortSessionIds,
        downloadingSessionId,
        handleAbortSession,
        handleDownloadReport,
      ],
    )

    const sessionMenusColumns = buildSessionMenusColumns()

    return (
      <>
        <Panel margin="small no">
          <Button
            type="primary"
            icon="play-circle"
            loading={isStartingSession}
            onClick={handleStartSession}
            margin="no small no no"
          >
            Start new session
          </Button>
          <Button
            icon="download"
            loading={downloadingMenuChangeDetections}
            onClick={handleDownloadMenuChangeDetections}
          >
            Menu change frequency report
          </Button>
        </Panel>

        <Panel margin="small no">
          <Typography.Title level={4}>Sessions</Typography.Title>
          <Table
            rowKey="id"
            loading={isLoadingSessions}
            columns={sessionColumns}
            dataSource={sessionItems}
            pagination={{
              current: sessionsPage,
              total: sessionsTotal,
              pageSize: SESSIONS_TABLE_PAGE_SIZE,
              onChange: page => setSessionsPage(page),
            }}
            onRow={session => ({
              onClick: () => setSelectedSessionId(session.id),
              style:
                session.id === selectedSessionId
                  ? { backgroundColor: '#e6f7ff', cursor: 'pointer' }
                  : { cursor: 'pointer' },
            })}
          />
        </Panel>

        <Panel margin="small no">
          <Typography.Title level={4}>Session details</Typography.Title>
          <Table
            rowKey="menuId"
            loading={isLoadingSessionMenus}
            columns={sessionMenusColumns}
            dataSource={selectedSessionId ? sessionMenus : []}
            pagination={{
              current: sessionMenusPage,
              total: sessionMenusTotal,
              pageSize: SESSION_DETAILS_TABLE_PAGE_SIZE,
              onChange: page => setSessionMenusPage(page),
              showSizeChanger: false,
            }}
            locale={{
              emptyText: selectedSessionId
                ? 'No menus found for the selected session.'
                : 'Select a session to see its menus.',
            }}
          />
        </Panel>
      </>
    )
  }

  return CrawlingSessionTab
}

export default createCrawlingSessionTab
